'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import {
  applyTheme,
  isThemePreference,
  type ResolvedTheme,
  readThemePreference,
  resolveTheme,
  THEME_STORAGE_KEY,
  type ThemePreference,
} from '@/lib/theme';

const ThemeContext = createContext<{
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (value: ThemePreference) => void;
} | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>('system');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('dark');

  const sync = useCallback((value: ThemePreference) => {
    const theme = resolveTheme(value, window.matchMedia('(prefers-color-scheme: dark)').matches);
    setPreferenceState(value);
    setResolvedTheme(theme);
    applyTheme(theme);
  }, []);

  useEffect(() => {
    sync(readThemePreference());
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) sync(readThemePreference());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [sync]);

  useEffect(() => {
    if (preference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => sync('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference, sync]);

  const setPreference = useCallback(
    (value: ThemePreference) => {
      if (!isThemePreference(value)) return;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, value);
      } catch {
        // The selection remains usable for this session when storage is blocked.
      }
      sync(value);
    },
    [sync],
  );

  return <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme requires ThemeProvider');
  return context;
}
